import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Download, Filter, Loader2, Receipt, CheckSquare, Square } from 'lucide-react';
import api from '../services/api';

const statusBadge = (status) => {
  const map = {
    0: { label: 'For Submission', color: 'bg-slate-100 text-slate-600' },
    1: { label: 'Unpaid', color: 'bg-yellow-100 text-yellow-700' },
    2: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  };
  const s = map[status] || map[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.color}`}>{s.label}</span>;
};

export default function ExportXeroInvoice() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filtered, setFiltered] = useState(false);

  const handleFilter = async () => {
    if (!startDate || !endDate) {
      toast.error('Please fill in the date range.');
      return;
    }
    setLoading(true);
    setFiltered(false);
    try {
      const res = await api.get(`/api/invoices/export-preview?start_date=${startDate}&end_date=${endDate}`);
      setInvoices(res.data);
      setSelected(new Set());
      setFiltered(true);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === invoices.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(invoices.map(i => i.id)));
    }
  };

  const handleExport = async () => {
    if (selected.size === 0) {
      toast.error('Please select at least one invoice to export.');
      return;
    }
    setExporting(true);
    try {
      const res = await api.post('/api/invoices/export-csv',
        { invoice_ids: Array.from(selected) },
        { responseType: 'blob' }
      );

      // Download CSV
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Xero invoice ${startDate} to ${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${selected.size} invoice(s) exported successfully`);

      // Mark exported invoices in local state (keep payment status unchanged)
      setInvoices(prev =>
        prev.map(inv =>
          selected.has(inv.id)
            ? { ...inv, is_exported: true }
            : inv
        )
      );
      setSelected(new Set());
    } catch (err) {
      toast.error('Failed to export invoices');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="export-xero-page">
      <div className="flex items-center gap-4">
        <Link to="/invoices" className="text-slate-500 hover:text-slate-700">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Export Xero Invoice</h1>
          <p className="text-slate-500 text-sm mt-1">Select invoices to export as CSV for Xero import</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[10px] border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded text-sm w-44"
              data-testid="export-start-date"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded text-sm w-44"
              data-testid="export-end-date"
            />
          </div>
          <button
            onClick={handleFilter}
            disabled={loading}
            className="px-5 py-2 bg-slate-600 text-white rounded text-sm font-medium hover:bg-slate-700 flex items-center gap-2"
            data-testid="export-filter-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Filter size={16} />}
            Filter
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || selected.size === 0}
            className="px-5 py-2 bg-amber-500 text-white rounded text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="export-btn"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Export
          </button>
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white rounded-[10px] border overflow-hidden">
        <table className="w-full text-sm" data-testid="export-invoices-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left w-10">
                <button onClick={toggleAll} className="text-slate-500 hover:text-slate-700" data-testid="export-check-all">
                  {invoices.length > 0 && selected.size === invoices.length
                    ? <CheckSquare size={18} />
                    : <Square size={18} />
                  }
                </button>
              </th>
              <th className="px-4 py-3 text-left font-semibold text-slate-600">Invoice No.</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-600">Invoice Date</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-600">Contact Name</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-600">Description</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-600">Status</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600">Unit Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {!filtered ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                  Filtered invoices will display here...
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <Loader2 className="animate-spin mx-auto text-primary" size={28} />
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <Receipt size={48} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">No invoices found for this date range</p>
                </td>
              </tr>
            ) : invoices.map(inv => (
              <tr
                key={inv.id}
                className={`hover:bg-orange-50/30 transition-colors cursor-pointer ${selected.has(inv.id) ? 'bg-amber-50' : ''}`}
                onClick={() => toggleSelect(inv.id)}
                data-testid={`export-row-${inv.id}`}
              >
                <td className="px-4 py-3">
                  <button onClick={e => { e.stopPropagation(); toggleSelect(inv.id); }}>
                    {selected.has(inv.id)
                      ? <CheckSquare size={18} className="text-amber-600" />
                      : <Square size={18} className="text-slate-400" />
                    }
                  </button>
                </td>
                <td className="px-4 py-3 font-medium">{inv.invoice_number}</td>
                <td className="px-4 py-3">{formatDate(inv.invoice_date)}</td>
                <td className="px-4 py-3">{inv.contact_name}</td>
                <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{inv.description}</td>
                <td className="px-4 py-3 text-center">
                  {statusBadge(inv.status)}
                  {inv.is_exported && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">Exported</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold">${(inv.unit_amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
